// API Configuration
// You can easily change these URLs by modifying the values below
const API_CONFIG = {
  // Main API base URL for most endpoints
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://dapi.hosnedl.cloud/api/',

  // Maps service base URL for tile services and map-related endpoints
  MAPS_BASE_URL: import.meta.env.VITE_MAPS_BASE_URL || 'https://maps.hosnedl.cloud/',

  // Exported files base URL (PDF/JPG/QR/offline package artifacts under exported_maps/)
  EXPORTED_MAPS_BASE_URL: import.meta.env.VITE_EXPORTED_MAPS_BASE_URL || 'https://nbg1.your-objectstorage.com/qrmaps/',

  // Backup tile server for map users (public access)
  TILES_BASE_URL: import.meta.env.VITE_TILES_BASE_URL || 'https://tiles.qrmaps.me/',

  // Special API base URL for specific endpoints that need different server
  SPECIAL_API_BASE_URL: import.meta.env.VITE_SPECIAL_API_BASE_URL || 'https://mapi.hosnedl.cloud/api/',

  // Staff monitoring targets the deployed clusters even when normal API calls
  // are redirected to localhost for development. Override for isolated tests.
  SYSTEM_ACTIVITY_API_BASE_URL: import.meta.env.VITE_SYSTEM_ACTIVITY_API_BASE_URL || 'https://dapi.hosnedl.cloud/api/',
  SYSTEM_ACTIVITY_MAP_API_BASE_URL: import.meta.env.VITE_SYSTEM_ACTIVITY_MAP_API_BASE_URL || 'https://mapi.hosnedl.cloud/api/',

  // List of endpoints that should use the special API base URL
  SPECIAL_ENDPOINTS: [
    'export_map',
    'bulk_map_export',
    'bulk_map_export_job',
    'export_pdf_map',
    'highlight_building',
    'delete_ib',
    'list_buildings',
    'ships_locations',
    'ships_history',
    'remote-printing',
    'tiles/',
    'tile-usage',
    'generate-offline-package',
    'offline-package-job',
    'distance_over_river',
    'pegel_locations',
    'pegel_current_level',
    'pegel_historical',
    'nearest_river_marker',
    'nearest_water_bodies',
    'bathymetry_depth',
    'sea_route',
  ],

  // Map Feature Configuration
  // Enable marker clustering to group nearby markers together (reduces clutter)
  ENABLE_MARKER_CLUSTERING: import.meta.env.VITE_ENABLE_MARKER_CLUSTERING !== 'false', // Default: true

  // Clustering parameters (only used when ENABLE_MARKER_CLUSTERING is true)
  CLUSTER_DISTANCE: parseInt(import.meta.env.VITE_CLUSTER_DISTANCE) || 40, // Pixels within which markers will be clustered
  CLUSTER_MIN_DISTANCE: parseInt(import.meta.env.VITE_CLUSTER_MIN_DISTANCE) || 20 // Minimum distance between clusters
};

// Helper function to get the correct base URL for an endpoint
export const getApiBaseUrl = (endpoint) => {
  // Check if the endpoint is in the special endpoints list
  const isSpecialEndpoint = API_CONFIG.SPECIAL_ENDPOINTS.some(specialEndpoint =>
    endpoint.includes(specialEndpoint)
  );

  return isSpecialEndpoint ? API_CONFIG.SPECIAL_API_BASE_URL : API_CONFIG.API_BASE_URL;
};

// Helper function to build full API URL
export const buildApiUrl = (endpoint) => {
  // Handle null, undefined, or empty endpoint
  if (!endpoint) {
    return '#';
  }

  const baseUrl = getApiBaseUrl(endpoint);
  // Remove leading slash from endpoint if it exists, since baseUrl already ends with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}${cleanEndpoint}`;
};

export const buildApiHostUrl = (endpoint) => {
  if (!endpoint) return '#';

  const apiUrl = new URL(API_CONFIG.API_BASE_URL);
  apiUrl.pathname = '/';
  apiUrl.search = '';
  apiUrl.hash = '';
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return new URL(cleanEndpoint, apiUrl).href;
};

// Helper function to build maps URL
export const buildMapsUrl = (endpoint) => {
  // Handle null, undefined, or empty endpoint
  if (!endpoint) {
    return '#';
  }

  // If endpoint is already a full URL (starts with http:// or https://), return as-is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  if (cleanEndpoint.startsWith('exported_maps/')) {
    return `${API_CONFIG.EXPORTED_MAPS_BASE_URL}${cleanEndpoint}`;
  }

  // Remove leading slash from endpoint if it exists, since MAPS_BASE_URL already ends with /
  return `${API_CONFIG.MAPS_BASE_URL}${cleanEndpoint}`;
};

export const buildExportedMapsUrl = (endpoint) => {
  if (!endpoint) {
    return '#';
  }

  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_CONFIG.EXPORTED_MAPS_BASE_URL}${cleanEndpoint}`;
};

// Helper function to build backup tiles URL (for map users)
export const buildTilesUrl = (endpoint) => {
  // Handle null, undefined, or empty endpoint
  if (!endpoint) {
    return '#';
  }

  // Remove leading slash from endpoint if it exists, since TILES_BASE_URL already ends with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_CONFIG.TILES_BASE_URL}${cleanEndpoint}`;
};

// Export the configuration for direct access if needed
export { API_CONFIG };
